import { supabase } from '../../supabase';
import { Tasker, Tasks } from '../../utils/types';
import { generateTaskNotificationEmail } from '../../email-templates/taskNotificationTemplate';
import { handleAssignedStatus } from '../../utils/emails/askers/statusEmails/assigned';
import { handleCompletedStatus } from '../../utils/emails/askers/statusEmails/complete';
import { handleAcceptedStatus } from '../../utils/emails/askers/statusEmails/accepted';
import { handleRejectedStatus } from '../../utils/emails/askers/statusEmails/rejected';
import { EmailAttachment } from '../../types/emails';
import { translateStatus } from '../../utils/emails/askers/statusEmails/statusHelpers';
import { formatPrice as formatMoney } from '../../utils/functions';
import { formatChileDateForEmail } from '../../utils/dates';
import { EmailData } from './emailSender';

export const sendStatusChangeEmail = async ({
  task,
  address,
}: {
  task: Tasks;
  address: string;
}) => {
  console.log('sendStatusChangeEmail called with task:', task);
  try {
    let emailContent: string;
    let subject: string;
    let attachments: EmailAttachment[] = [];

    switch (task.status) {
      case 'assigned':
        ({ emailContent, subject, attachments } = await handleAssignedStatus(task));
        break;
      case 'complete':
        ({ emailContent, subject } = await handleCompletedStatus(task));
        break;
      case 'accepted':
        ({ emailContent, subject } = await handleAcceptedStatus(task, address));
        break;
      case 'rejected':
        ({ emailContent, subject } = await handleRejectedStatus());
        break;
      default:
        emailContent = generateTaskNotificationEmail(task);
        subject = `Tu Task ahora está "${translateStatus(task.status)}"`;
    }

    const { data: askerData, error: askerError } = await supabase
      .from('askers')
      .select('email')
      .eq('id', task.asker_id)
      .single();

    if (askerError) {
      console.error('Error fetching asker data:', askerError);
      throw askerError;
    }

    const emailData: EmailData = {
      email: askerData?.email,
      subject: subject,
      content: emailContent,
    };

    if (attachments.length > 0) {
      emailData.attachments = attachments;
    }

    const { data, error } = await supabase.functions.invoke('sendEmail', {
      body: JSON.stringify(emailData),
    });

    if (error) {
      console.error('Error from supabase.functions.invoke:', error);
      throw error;
    }

    // Check if data contains an error message
    if (data && data.error) {
      console.error('Error from sendEmail function:', data.error);
      throw new Error(data.error);
    }

    // Check if the response is a success message
    if (data && data.message === 'Email sent successfully') {
      console.log('Email sent successfully, response:', data);
      return data;
    } else {
      console.error('Unexpected response format:', data);
      throw new Error('Unexpected response format');
    }
  } catch (error) {
    console.error('Error sending status change email:', error);
    throw error;
  }
};

export const sendTaskCreationEmailToAsker = async (taskData: Tasks) => {
  let dateText: string;

  if (taskData.is_asap) {
    dateText = 'Lo más pronto posible';
  } else if (taskData.is_flexible_date) {
    dateText = 'Fecha y hora flexible';
  } else if (taskData.date) {
    dateText = formatChileDateForEmail(taskData.date);
  } else {
    dateText = 'Sin especificar';
  }

  // do asker email
  const { data: askerData, error: askerError } = await supabase
    .from('askers')
    .select('email')
    .eq('id', taskData.asker_id)
    .single();

  if (askerError) {
    console.error('Error fetching asker data:', askerError);
    throw askerError;
  }

  const emailDataToAsker = {
    email: askerData?.email,
    subject: 'Confirmación de tu nueva Task en Tasky',
    content: `
      <h1>¡Gracias por crear una nueva tarea en Tasky!</h1>
      <p>Hola ${taskData.name},</p>
      <p>Tu tarea ha sido creada exitosamente. Aquí están los detalles:</p>
      <ul>
        <li><strong>Descripción:</strong> ${taskData.description}</li>
        <li><strong>Categoría:</strong> ${taskData.category}</li>
        <li><strong>Precio:</strong> ${formatMoney(taskData.price)}</li>
        <li><strong>Fecha:</strong> ${dateText}</li>
      </ul>
      <p>Nos pondremos en contacto contigo pronto para confirmar los detalles y asignar un tasker.</p>
      <p>Si tienes alguna pregunta, no dudes en contactarnos.</p>
      <p>¡Gracias por usar Tasky!</p>
    `,
  };

  try {
    const { data, error } = await supabase.functions.invoke('sendEmail', {
      body: JSON.stringify(emailDataToAsker),
    });

    if (error) {
      console.error('Error sending task creation email to asker:', error);
      throw error;
    }

    console.log('Confirmation email sent successfully to asker');
    return data;
  } catch (error) {
    console.error('Error sending task creation email to asker:', error);
    throw error;
  }
};

export const sendAskerPriceProposalEmail = async ({
  task,
  tasker,
  originalPrice,
  proposedPrice,
}: {
  task: Tasks;
  tasker: Tasker;
  originalPrice: number;
  proposedPrice: number;
}) => {
  const priceDifference = proposedPrice - originalPrice;
  const percentageDifference = ((priceDifference / originalPrice) * 100).toFixed(1);

  const emailData = {
    email: task.email,
    subject: 'Nueva propuesta de precio para tu Task',
    content: `
    <table width="100%" cellspacing="0" cellpadding="0" style="background-color: #f3f4f6; font-family: Arial, 'Helvetica Neue', sans-serif;">
      <tr>
        <td align="center" style="padding: 20px 0;">
          <table width="100%" cellspacing="0" cellpadding="0" style="max-width: 600px; border-radius: 10px; overflow: hidden; background-color: white">
            <tr>
              <td>
                <img src="https://lefgpghkpyydyoooswkw.supabase.co/storage/v1/object/public/dev/common/email-top-assigned.png" alt="Tasky Header" width="100%" style="display: block; max-width: 100%;">
              </td>
            </tr>
            <tr>
              <td bgcolor="#ffffff" style="border-bottom-left-radius: 10px; border-bottom-right-radius: 10px;">
                <table width="100%" cellspacing="0" cellpadding="0">
                  <tr>
                    <td style="padding: 40px 20px 20px;">
                      <h1 style="text-align: center; color: #333333; margin-top: 0; margin-bottom: 40px;">¡Tienes una nueva propuesta para tu Task!</h1>

                      <p style="color: #333333; font-size: 18px; margin-bottom: 30px;">Hola ${task.name},</p>
                      <p style="color: #333333; font-size: 18px; margin-bottom: 30px;">Un Tasker ha propuesto un precio diferente para realizar tu Task.</p>

                      <!-- Task Details Card -->
                      <table width="100%" cellspacing="0" cellpadding="0" style="background-color: #f3f4f6; border-radius: 10px; margin-bottom: 30px;">
                        <tr>
                          <td style="padding: 20px;">
                            <h2 style="color: #333333; font-size: 20px; margin: 0 0 15px 0;">Detalles de la Task</h2>
                            <p style="color: #666666; margin: 5px 0;"><strong>Descripción:</strong> ${task.description}</p>
                            <p style="color: #666666; margin: 5px 0;"><strong>Categoría:</strong> ${task.category}</p>

                            <!-- Price Comparison Box -->
                            <table width="100%" cellspacing="0" cellpadding="0" style="background-color: white; border-radius: 8px; margin-top: 15px;">
                              <tr>
                                <td style="padding: 15px;">
                                  <p style="color: #666666; margin: 5px 0;">
                                    <strong>Precio original:</strong>
                                    <span style="text-decoration: line-through;">${formatMoney(originalPrice)}</span>
                                  </p>
                                  <p style="color: #8B5CF6; font-size: 18px; font-weight: bold; margin: 5px 0;">
                                    <strong>Precio propuesto:</strong> ${formatMoney(proposedPrice)}
                                  </p>
                                  <p style="color: ${priceDifference > 0 ? '#ef4444' : '#22c55e'}; margin: 5px 0;">
                                    <strong>Diferencia:</strong> ${priceDifference > 0 ? '+' : ''}${formatMoney(priceDifference)}
                                    (${priceDifference > 0 ? '+' : ''}${percentageDifference}%)
                                  </p>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </table>

                      <!-- Tasker Info Card -->
                      <table width="100%" cellspacing="0" cellpadding="0" style="background-color: #f3f4f6; border-radius: 10px; margin-bottom: 30px;">
                        <tr>
                          <td style="padding: 20px;">
                            <h2 style="color: #333333; font-size: 20px; margin: 0 0 15px 0;">Información del Tasker</h2>
                            <table width="100%" cellspacing="0" cellpadding="0">
                              <tr>
                                <td style="width: 60px;">
                                  <img src="${tasker.photo_selfie_url}" alt="${tasker.name}" width="60" height="60" style="border-radius: 50%; display: block;">
                                </td>
                                <td style="padding-left: 15px;">
                                  <p style="color: #333333; font-size: 18px; font-weight: bold; margin: 0;">${tasker.name} ${tasker.last_name}</p>
                                  <p style="color: #666666; margin: 5px 0;">${tasker.university}</p>
                                  <p style="color: #666666; margin: 5px 0;">${tasker.career}</p>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </table>

                      <!-- Action Buttons -->
                      <table width="100%" cellspacing="0" cellpadding="0" style="margin: 30px 0;">
                        <tr>
                          <td align="center">
                            <table cellspacing="0" cellpadding="0">
                              <tr>
                                <td style="border-radius: 4px;" bgcolor="#22c55e">
                                  <a href="https://askers.taskyapp.cl/task/${task.id}/accept-proposal/${tasker.id}"
                                     target="_blank"
                                     style="padding: 12px 24px; border-radius: 4px; color: white; text-decoration: none; display: inline-block; font-weight: bold; font-size: 16px;">
                                    Aceptar propuesta
                                  </a>
                                </td>
                                <td style="width: 20px;"></td>
                                <td style="border-radius: 4px;" bgcolor="#ef4444">
                                  <a href="https://askers.taskyapp.cl/task/${task.id}/reject-proposal/${tasker.id}"
                                     target="_blank"
                                     style="padding: 12px 24px; border-radius: 4px; color: white; text-decoration: none; display: inline-block; font-weight: bold; font-size: 16px;">
                                    Rechazar propuesta
                                  </a>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </table>

                      <!-- Footer -->
                      <table width="100%" cellspacing="0" cellpadding="0" bgcolor="#8B5CF6" style="margin-top: 40px; padding: 20px; border-radius: 10px;">
                        <tr>
                          <td align="center">
                            <img src="https://lefgpghkpyydyoooswkw.supabase.co/storage/v1/object/public/dev/common/tasky-logo-dark-theme.png" alt="Tasky Logo" width="80" style="display: block; margin: 0 auto;">
                            <div style="margin-top: 20px;">
                              <a href="https://www.instagram.com/tasky.cl" style="margin: 0 10px; outline: none; text-decoration: none;">
                                <img src="https://lefgpghkpyydyoooswkw.supabase.co/storage/v1/object/public/dev/common/instagram-icon.png" alt="Instagram" width="24">
                              </a>
                              <a href="https://www.linkedin.com/company/taskyapp/" style="margin: 0 10px; outline: none; text-decoration: none;">
                                <img src="https://lefgpghkpyydyoooswkw.supabase.co/storage/v1/object/public/dev/common/linkedin-icon.png" alt="LinkedIn" width="24">
                              </a>
                              <a href="https://www.tiktok.com/@tasky.cl" style="margin: 0 10px; outline: none; text-decoration: none;">
                                <img src="https://lefgpghkpyydyoooswkw.supabase.co/storage/v1/object/public/dev/common/tiktok-icon.png" alt="TikTok" width="24">
                              </a>
                            </div>
                            <p style="color: #ffffff; font-size: 11px; margin-top: 10px;">Pide tu Task y universitarios resuelven</p>
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
    `,
  };

  try {
    const { data, error } = await supabase.functions.invoke('sendEmail', {
      body: JSON.stringify(emailData),
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error sending price proposal email:', error);
    throw error;
  }
};
